import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import { Box, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FLAG_WINDOW_MS } from '../../redux/features/issueReport/issueReport.slice';
import Colors from '../../utils/color';

interface FlagWindowDialProps {
  /** Absolute deadline, or null while paused. */
  expiresAt: number | null;
  /** Banked remainder while paused. */
  remainingMs: number | null;
  isPaused: boolean;
  onUndo: () => void;
}

const DIAL_SIZE = 56;

/**
 * The countdown, with Undo INSIDE the ring so the sweep visibly depletes around the
 * action it bounds.
 *
 * ONE ring for both meanings. Undo and auto-close are the same deadline off the same
 * anchor (`created_at + FLAG_WINDOW_MS`), so rendering them separately could only
 * ever show the agent two numbers for one moment.
 *
 * The number and the sweep are both DERIVED from `expiresAt`; the interval below only
 * forces a re-render. A throttled tab therefore shows a truthful remaining time
 * rather than a stretched one.
 *
 * ## The paused state needs static signals
 *
 * A frozen ring is indistinguishable from a running one at a glance — motion is the
 * only difference, and there is none in a still frame. So pausing also shifts the
 * numeral to amber and swaps in a pause glyph. Exactly two signals: fewer and the
 * hold is invisible, more (dashed arc, halo, badge) and it reads as an error.
 */
const FlagWindowDial = ({
  expiresAt,
  remainingMs,
  isPaused,
  onUndo,
}: FlagWindowDialProps) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (isPaused || !expiresAt) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [isPaused, expiresAt]);

  const msLeft = isPaused
    ? remainingMs ?? 0
    : Math.max(0, (expiresAt ?? 0) - Date.now());
  const secondsLeft = Math.max(0, Math.ceil(msLeft / 1000));
  const progress = Math.max(0, Math.min(100, (msLeft / FLAG_WINDOW_MS) * 100));

  return (
    <Box
      sx={{
        position: 'relative',
        width: DIAL_SIZE,
        height: DIAL_SIZE,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      data-testid="flag-window-dial"
      data-paused={isPaused ? 'true' : 'false'}
    >
      {/* Track, so the depleted arc is legible rather than absent. */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={DIAL_SIZE}
        thickness={3}
        // Decorative: must not intercept the pointer, or it covers the button.
        sx={{
          position: 'absolute',
          color: 'rgba(255,255,255,0.18)',
          pointerEvents: 'none',
        }}
      />
      <CircularProgress
        variant="determinate"
        value={progress}
        size={DIAL_SIZE}
        thickness={3}
        sx={{
          position: 'absolute',
          color: isPaused ? Colors.amber : Colors.green,
          pointerEvents: 'none',
        }}
      />
      <Box
        component="button"
        type="button"
        onClick={onUndo}
        aria-label={`Undo, ${secondsLeft} seconds left`}
        sx={{
          all: 'unset',
          // `all: unset` makes this static, which would let the absolutely
          // positioned rings above paint over it. Both are pointer-events:none, and
          // this is raised as well so the whole face is clickable.
          position: 'relative',
          zIndex: 1,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: DIAL_SIZE - 12,
          height: DIAL_SIZE - 12,
          borderRadius: '50%',
          // `all: unset` resets `outline`, so without this Undo took keyboard focus
          // invisibly. `:focus-visible` keeps the ring off mouse presses.
          '&:focus-visible': {
            outline: `2px solid ${Colors.white}`,
            outlineOffset: '2px',
          },
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '1px',
            color: isPaused ? Colors.amber : Colors.white,
            fontWeight: 700,
            fontSize: '0.85rem',
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          {isPaused && <PauseRoundedIcon sx={{ fontSize: 12 }} />}
          {secondsLeft}
        </Box>
        <Box
          component="span"
          sx={{
            color: Colors.white,
            fontSize: '0.56rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            lineHeight: 1,
            mt: '2px',
            cursor: 'pointer',
          }}
        >
          UNDO
        </Box>
      </Box>
    </Box>
  );
};

/** Memoized for the same reason as IssueTypeChips — `onUndo` is a stable useCallback. */
export default React.memo(FlagWindowDial);
